import { Box, Paper, Stack, Tab, Tabs, TextField, MenuItem, Select, InputLabel, FormControl, FormControlLabel, Switch, Button, IconButton, Tooltip, Alert } from "@mui/material"
import { DataGrid, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import { DatePicker } from '@mui/x-date-pickers';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';

import { useEffect, useState } from "react";
import moment from 'moment'

import useFetch from './api/api'
import OperationLogs from "./OperationLogs";
import Loading from "./components/Loading";
import config from "./common/Config";

const fastQueueColumns = [
	{ field: 'ticker',      headerName: 'Ticker',     headerAlign: 'center', align: 'center', disableColumnMenu: true },
	{ field: 'type',        headerName: 'Tipo',       headerAlign: 'center', align: 'center', disableColumnMenu: true,
		valueFormatter: (v) => v === 'ACAO' ? 'Ação' : v === 'STOCK' ? 'Stock' : v },
	{ field: 'requestedAt', headerName: 'Solicitado', headerAlign: 'center', align: 'center', disableColumnMenu: true,
		valueFormatter: (v) => v ? moment(v).format('DD/MM HH:mm:ss') : '—' },
	{ field: 'processedAt', headerName: 'Processado', headerAlign: 'center', align: 'center', disableColumnMenu: true,
		valueFormatter: (v) => v ? moment(v).format('DD/MM HH:mm:ss') : '—' },
	{ field: 'status',      headerName: 'Status',     headerAlign: 'center', align: 'center', disableColumnMenu: true },
	{ field: 'message',     headerName: 'Mensagem',   headerAlign: 'center', align: 'left',   disableColumnMenu: true, flex: 1 },
]

const FastQueueGrid = () => {
	const privateFetch                        = useFetch()
	const [allItems, setAllItems]             = useState()
	const [filteredItems, setFilteredItems]   = useState()
	const [status, setStatus]                 = useState('ALL')
	const [returnStatus, setReturnStatus]     = useState(0)
	const [returnMessage, setReturnMessage]   = useState('')
	const [newTicker, setNewTicker]           = useState('')
	const [newType, setNewType]               = useState('ACAO')
	const [selected, setSelected]             = useState([])

	useEffect(() => { loadFastQueue() }, [])

	const loadFastQueue = () => {
		privateFetch(config.apiUrl + '/api/operations/fast-queue')
			.then(d => d.status === 200 ? d.json() : [])
			.then(d => {
				const items = Array.isArray(d) ? d : []
				setAllItems(items)
				setFilteredItems(items)
				setSelected([])
			})
	}

	const handleFilter = () => {
		setFilteredItems(allItems.filter(i => status === 'ALL' || i.status === status))
	}

	const handleAdd = () => {
		if (!newTicker.trim()) return
		const options = {
			method: 'POST',
			headers: { 'content-type': 'application/json; charset=utf-8' },
			body: JSON.stringify({ items: [{ ticker: newTicker.trim().toUpperCase(), type: newType }] })
		}
		privateFetch(config.apiUrl + '/api/operations/fast-queue', options)
			.then(d => {
				const ok = d.status === 200
				ok ? setReturnStatus(1) : setReturnStatus(2)
				if (ok) setNewTicker('')
				return d.json()
			})
			.then(d => { setReturnMessage(d); loadFastQueue() })
	}

	const handleRequeue = () => {
		if (!selected.length) return
		const options = {
			method: 'POST',
			headers: { 'content-type': 'application/json; charset=utf-8' },
			body: JSON.stringify({ ids: selected })
		}
		privateFetch(config.apiUrl + '/api/operations/fast-queue/requeue', options)
			.then(d => {
				d.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
				return d.json()
			})
			.then(d => { setReturnMessage(d); loadFastQueue() })
	}

	const handleRemoveSelected = () => {
		if (!selected.length) return
		const options = {
			method: 'DELETE',
			headers: { 'content-type': 'application/json; charset=utf-8' },
			body: JSON.stringify({ ids: selected })
		}
		privateFetch(config.apiUrl + '/api/operations/fast-queue', options)
			.then(d => {
				d && d.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
				return d.status === 200 ? d.json() : ''
			})
			.then(d => { setReturnMessage(d); setSelected([]); loadFastQueue() })
	}

	const handleClear = () => {
		privateFetch(config.apiUrl + '/api/operations/fast-queue', { method: 'DELETE', headers: { 'content-type': 'application/json; charset=utf-8' }, body: JSON.stringify({}) })
			.then(d => {
				d && d.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
				return d.status === 200 ? d.json() : ''
			})
			.then(d => { setReturnMessage(d); loadFastQueue() })
	}

	const FastQueueFooter = () => (
		<GridFooterContainer>
			<Stack direction="row" marginLeft={0.5}>
				<Tooltip title="Reprocessar">
					<span><IconButton onClick={handleRequeue} disabled={!selected.length}><UploadIcon color={selected.length ? 'warning' : 'disabled'} /></IconButton></span>
				</Tooltip>
				<Tooltip title="Remover selecionados">
					<span><IconButton onClick={handleRemoveSelected} disabled={!selected.length}><DeleteIcon /></IconButton></span>
				</Tooltip>
				<Tooltip title="Atualizar dados"><IconButton onClick={loadFastQueue}><RefreshIcon /></IconButton></Tooltip>
				<Tooltip title="Limpar processados/erros (> 24h)"><IconButton onClick={handleClear}><CleaningServicesIcon /></IconButton></Tooltip>
			</Stack>
			<GridFooter />
		</GridFooterContainer>
	)

	return (
		<Stack spacing={2} margin={1} mt={2} sx={{ flex: 1 }}>
			{returnStatus > 0 && (
				<Alert severity={returnStatus === 1 ? 'success' : 'error'} onClose={() => setReturnStatus(0)}>
					{returnMessage}
				</Alert>
			)}
			<Stack direction="row" alignItems="center" flexWrap="wrap">
				{/* Filtros — lado esquerdo */}
				<Stack direction="row" spacing={1} alignItems="center">
					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel>Status</InputLabel>
						<Select label="Status" value={status} onChange={e => setStatus(e.target.value)}>
							<MenuItem value="ALL">Todos</MenuItem>
							<MenuItem value="WAI">Aguardando Processamento</MenuItem>
							<MenuItem value="PRC">Processado</MenuItem>
							<MenuItem value="ERR">Erro</MenuItem>
						</Select>
					</FormControl>
					<Button variant="contained" onClick={handleFilter}>Filtrar</Button>
				</Stack>

				<Box sx={{ flex: 1 }} />

				{/* Adicionar — lado direito */}
				<Stack direction="row" spacing={1} alignItems="center">
					<TextField
						size="small" label="Ticker" value={newTicker} sx={{ width: 130 }}
						onChange={e => setNewTicker(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && handleAdd()}
					/>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel>Tipo</InputLabel>
						<Select value={newType} label="Tipo" onChange={e => setNewType(e.target.value)}>
							<MenuItem value="ACAO">Ação</MenuItem>
							<MenuItem value="STOCK">Stock</MenuItem>
						</Select>
					</FormControl>
					<Button variant="contained" onClick={handleAdd}>Adicionar</Button>
				</Stack>
			</Stack>
			{filteredItems !== undefined
				? <DataGrid
					getRowId={(row) => row.id}
					rows={filteredItems}
					columns={fastQueueColumns}
					slots={{ footer: FastQueueFooter }}
					initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
					pageSizeOptions={[10, 20, 50]}
					rowHeight={30}
					checkboxSelection
					onRowSelectionModelChange={ids => setSelected(ids)}
				/>
				: <Loading sx={{ flex: 1 }} />
			}
		</Stack>
	)
}

const OperationPanel = () => {
	const [currentTabIndex, setCurrentTabIndex]	= useState(0)
	const [queueItems, setQueueItems] 			= useState();

	const privateFetch = useFetch();

	useEffect(() => {
		loadQueueItems()
	},[])

	const handleTabChange = (e, tabIndex) => {
		if (tabIndex === 0) loadQueueItems()
		setCurrentTabIndex(tabIndex);
	};

	const loadQueueItems = async() => {
		privateFetch(config.apiUrl +'/api/operations/queue')
		.then(data => data.json())
		.then(data => setQueueItems(data))
	}

	const QueueItemsGrid = () => {
		const columns = [
			{ field : 'ticker', headerName : 'Ticker', headerAlign : 'center', align : 'center', sortable : false, disableColumnMenu : true},
			{ field : 'type', headerName : 'Tipo', headerAlign : 'center', align : 'center', sortable : false, disableColumnMenu : true},
			{ field : 'date', headerName : 'Data', headerAlign : 'center', align : 'center', sortable : false, disableColumnMenu : true},
			{ field : 'status', headerName : 'Status', headerAlign : 'center', align : 'center', sortable : false, disableColumnMenu : true},
			{ field : 'errorCount', headerName : 'Erros', headerAlign : 'center', align : 'center', sortable : false, disableColumnMenu : true},
			{ field : 'message', headerName : 'Mensagem', headerAlign : 'center', align : 'left', sortable : false, disableColumnMenu : true, width:800},
		]

		return (
			<Stack margin={1} mt={2} sx={{ flex: 1 }}>
				{queueItems
					? <DataGrid
						getRowId={(row) => row.id}
						rows={queueItems}
						columns={columns}
						initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
						pageSizeOptions={[5, 10, 20]}
						rowHeight={30}
					/>
					: <Loading sx={{ flex: 1 }} />
				}
			</Stack>
		)
	}

	const QueueErrorsGrid = () => {
		const [errorItems, setErrorItems] 				= useState()
		const [originalItems, setOriginalItems] 		= useState()

		const [initialDate, setInitialDate] 			= useState()
		const [finalDate, setFinalDate] 				= useState()
		const [type, setType]							= useState('')
		const [status, setStatus]						= useState('')
		const [errorCount, setErrorCount]				= useState('')
		
		const [checkboxSelection, setCheckboxSelection]	= useState()
		const [returnStatus, setReturnStatus] 		= useState(0)
		const [returnMessage, setReturnMessage]		= useState('')

		useEffect(() => {
			loadQueueErrors()
		},[])

		const loadQueueErrors = async() => {
			privateFetch(config.apiUrl +'/api/operations/errors')
			.then(data => data.json())
			.then(data => {
				setErrorItems(data)
				setOriginalItems(data)
			})	
		}

		const handleClear = () => {
			setInitialDate()
			setFinalDate()
			setType('')
			setStatus('')
			setErrorCount('')
			setErrorItems(originalItems)
		}

		const handleFilter = () => {
			let filteredItems = originalItems

			if (initialDate) {
				filteredItems = filteredItems.filter(o => moment(o.date) > moment(initialDate))
			}

			if (finalDate) {
				filteredItems = filteredItems.filter(o => moment(o.date) < moment(finalDate))
			}

			if (type.includes("EARNINGS")) { 
				filteredItems = filteredItems.filter(o => o.type === 'EARNINGS' && o.market === type.substring(9,12))
			}
			else if (type !== '') {
				filteredItems = filteredItems.filter(o => o.type === type)
			}

			if (status === 'WAR') { 
				filteredItems = filteredItems.filter(o => o.status === 'WAI' && o.message != null)
			}
			else if (status !== '') {
				filteredItems = filteredItems.filter(o => o.status === status)
			}

			if (errorCount) {
				filteredItems = filteredItems.filter(o => o.errorCount > errorCount)
			}

			setErrorItems(filteredItems)
		}

		const handleFastQueue = () => {
			const selected = errorItems.filter(o =>
				checkboxSelection.includes(o.id) && (o.type === 'ACAO' || o.type === 'STOCK')
			)
			if (!selected.length) {
				setReturnStatus(2)
				setReturnMessage('Selecione itens do tipo Ação ou Stock para processar na fila rápida')
				return
			}
			const options = {
				method: 'POST',
				headers: { 'content-type': 'application/json; charset=utf-8' },
				body: JSON.stringify({ items: selected.map(o => ({
					ticker  : o.ticker,
					type    : o.type,
					queueId : o.id,
					date    : o.date,
				})) })
			}
			privateFetch(config.apiUrl + '/api/operations/fast-queue', options)
				.then(data => {
					data && data.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
					return data.json()
				})
				.then(data => { setReturnMessage(data); loadQueueErrors() })
		}

		const handleRequeue = () => {
			const options = {
				method : 'POST',
				headers : {
					'content-type' : 'application/json; charset=utf-8',
				},
				body : JSON.stringify({
					updatedIds : checkboxSelection
				})
			}

			privateFetch(config.apiUrl +'/api/operations/requeue', options)
			.then(data => {
				data && data.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
				return data.json()
			})
			.then(data => {
				setReturnMessage(data)
				loadQueueErrors()
			})
		}

		const handleRemove = () => {
			
			const options = {
				method : 'DELETE',
				headers : {
					'content-type' : 'application/json; charset=utf-8',
				},
				body : JSON.stringify({
					ids : checkboxSelection
				})
			}

			privateFetch(config.apiUrl +'/api/queue/', options)
			.then(data => {
				data && data.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
				return data.json()
			})
			.then(data => {
				setReturnMessage(data)
				loadQueueErrors()
			})
		}

		const columns = [
			{ field : 'ticker', headerName : 'Ticker', headerAlign : 'center', align : 'center', disableColumnMenu : true},
			{ field : 'type', headerName : 'Tipo', headerAlign : 'center', align : 'center', disableColumnMenu : true},
			{ 
				field : 'date', 
				headerName : 'Data', 
				headerAlign : 'center', 
				align : 'center', 
				sortable : true, 
				disableColumnMenu : true,
				valueFormatter : (params) => moment(params).format("DD/MM/YYYY")

			},
			{ field : 'status', headerName : 'Status', headerAlign : 'center', align : 'center', disableColumnMenu : true},
			{ field : 'errorCount', headerName : 'Erros', headerAlign : 'center', align : 'center', disableColumnMenu : true},
			{ field : 'message', headerName : 'Mensagem', headerAlign : 'center', align : 'left', disableColumnMenu : true, width:800},
		]

		const QueueErrorsGridFooter = (props) => {
			return (
				<GridFooterContainer>
					<Stack direction={"row"} marginLeft={0.5}>
						<Tooltip title="Reinserir na fila"><IconButton onClick={handleRequeue}><UploadIcon/></IconButton></Tooltip>
						<Tooltip title="Processar agora (fila rápida)"><IconButton onClick={handleFastQueue}><UploadIcon color="warning"/></IconButton></Tooltip>
						<Tooltip title="Remover da fila"><IconButton onClick={handleRemove}><DeleteIcon/></IconButton></Tooltip>
						<Tooltip title="Atualizar dados"><IconButton onClick={() => loadQueueErrors()}><RefreshIcon/></IconButton></Tooltip>
					 </Stack>
					<GridFooter></GridFooter>
				</GridFooterContainer>
			)
		}

		return (
			<Stack spacing={2} margin={1} mt={2} sx={{ flex: 1 }}>
				{returnStatus > 0 && (
					<Alert severity={returnStatus === 1 ? 'success' : 'error'} onClose={() => setReturnStatus(0)}>{returnMessage}</Alert>
				)}

				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
					<DatePicker
						format="DD/MM/YYYY"
						label="Data Inicial"
						slotProps={{ textField: { size: 'small', sx: { width: 155 } }, field: { clearable: true } }}
						value={initialDate ? moment(initialDate) : null}
						onChange={(value) => setInitialDate(value)}
					/>
					<DatePicker
						format="DD/MM/YYYY"
						label="Data Final"
						slotProps={{ textField: { size: 'small', sx: { width: 155 } }, field: { clearable: true } }}
						value={finalDate ? moment(finalDate) : null}
						onChange={(value) => setFinalDate(value)}
					/>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Tipo</InputLabel>
						<Select label="Tipo" value={type} onChange={(e) => setType(e.target.value)}>
							<MenuItem value="">Todos</MenuItem>
							<MenuItem value="EARNINGS_BR">Dividendos BR</MenuItem>
							<MenuItem value="EARNINGS_US">Dividendos US</MenuItem>
							<MenuItem value="ACAO">Ações</MenuItem>
							<MenuItem value="STOCK">Stocks</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel>Status</InputLabel>
						<Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
							<MenuItem value="WAI">Quarentena</MenuItem>
							<MenuItem value="ERR">Erro</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel>Erros</InputLabel>
						<Select label="Erros" value={errorCount} onChange={(e) => setErrorCount(e.target.value)}>
							<MenuItem value={1}>Mais que 1</MenuItem>
							<MenuItem value={5}>Mais que 5</MenuItem>
							<MenuItem value={10}>Mais que 10</MenuItem>
							<MenuItem value={25}>Mais que 25</MenuItem>
						</Select>
					</FormControl>
					<Button variant="contained" onClick={handleFilter}>Filtrar</Button>
					<Button variant="contained" onClick={handleClear}>Limpar</Button>
				</Stack>

				{errorItems
					? <DataGrid
						getRowId={(row) => row.id}
						rows={errorItems}
						columns={columns}
						slots={{ footer: QueueErrorsGridFooter }}
						initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
						pageSizeOptions={[5, 10, 20]}
						rowHeight={30}
						checkboxSelection
						onRowSelectionModelChange={(selected) => setCheckboxSelection(selected)}
					/>
					: <Loading sx={{ flex: 1 }} />
				}
			</Stack>
		)
	}

	const PendingUpdatesGrid = () => {
		const [items, setItems]                         = useState()
		const [filteredItems, setFilteredItems]         = useState()
		const [ticker, setTicker]                       = useState('')
		const [type, setType]                           = useState('')
		const [country, setCountry]                     = useState('')
		const [checkboxSelection, setCheckboxSelection] = useState([])
		const [notQueued, setNotQueued]                 = useState(false)
		const [returnStatus, setReturnStatus]           = useState(0)
		const [returnMessage, setReturnMessage]         = useState('')

		useEffect(() => { loadItems() }, [])

		const loadItems = () => {
			privateFetch(config.apiUrl + '/api/operations/pending-updates')
				.then(d => d.json())
				.then(d => { setItems(d); setFilteredItems(d) })
		}

		const handleFilter = () => {
			let filtered = items
			if (ticker)    filtered = filtered.filter(o => o.ticker.includes(ticker.toUpperCase()))
			if (type)      filtered = filtered.filter(o => o.type === type)
			if (country)   filtered = filtered.filter(o => o.country === country)
			if (notQueued) filtered = filtered.filter(o => !o.queueStatus)
			setFilteredItems(filtered)
		}

		const handleClear = () => {
			setTicker(''); setType(''); setCountry(''); setNotQueued(false)
			setFilteredItems(items)
		}

		const handleEnqueue = () => {
			const selected = filteredItems.filter(o => checkboxSelection.includes(o.ticker + o.quarter))
			const options = {
				method: 'POST',
				headers: { 'content-type': 'application/json; charset=utf-8' },
				body: JSON.stringify({ items: selected.map(o => ({
					ticker:     o.ticker,
					market:     o.type === 'ACAO' ? 'BR' : 'US',
					date:       o.fiscalDateEnding,
					type:       o.type,
					status:     'WAI',
					errorCount: 0,
				})) })
			}
			privateFetch(config.apiUrl + '/api/operations/queue', options)
				.then(data => {
					data && data.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
					return data.json()
				})
				.then(data => { setReturnMessage(data); loadItems() })
		}

		const countries = items ? [...new Set(items.map(o => o.country).filter(Boolean))].sort() : []

		const handleFastQueue = () => {
			const selected = filteredItems.filter(o => checkboxSelection.includes(o.ticker + o.quarter))
			const options = {
				method: 'POST',
				headers: { 'content-type': 'application/json; charset=utf-8' },
				body: JSON.stringify({ items: selected.map(o => ({
					ticker : o.ticker,
					type   : o.type,
					date   : o.fiscalDateEnding,
				})) })
			}
			privateFetch(config.apiUrl + '/api/operations/fast-queue', options)
				.then(data => {
					data && data.status !== 200 ? setReturnStatus(2) : setReturnStatus(1)
					return data.json()
				})
				.then(data => { setReturnMessage(data); loadItems() })
		}

		const PendingUpdatesFooter = () => (
			<GridFooterContainer>
				<Stack direction="row" marginLeft={0.5}>
					<Tooltip title="Enfileirar na fila normal"><IconButton onClick={handleEnqueue}><UploadIcon /></IconButton></Tooltip>
					<Tooltip title="Processar agora (fila rápida)"><IconButton onClick={handleFastQueue}><UploadIcon color="warning" /></IconButton></Tooltip>
					<Tooltip title="Atualizar dados"><IconButton onClick={loadItems}><RefreshIcon /></IconButton></Tooltip>
				</Stack>
				<GridFooter />
			</GridFooterContainer>
		)

		const columns = [
			{ field: 'ticker',       headerName: 'Ticker',     headerAlign: 'center', align: 'center', disableColumnMenu: true },
			{ field: 'quarter',      headerName: 'Trimestre',  headerAlign: 'center', align: 'center', disableColumnMenu: true },
			{ field: 'releaseDate',  headerName: 'Divulgação', headerAlign: 'center', align: 'center', disableColumnMenu: true,
				valueFormatter: (v) => v ? moment(v).format('DD/MM/YYYY') : '—' },
			{ field: 'type',         headerName: 'Tipo',       headerAlign: 'center', align: 'center', disableColumnMenu: true },
			{ field: 'country',      headerName: 'País',       headerAlign: 'center', align: 'center', disableColumnMenu: true },
			{ field: 'queueStatus',  headerName: 'Fila',       headerAlign: 'center', align: 'center', disableColumnMenu: true,
				valueFormatter: (v) => v ?? '—' },
			{ field: 'errorCount',   headerName: 'Erros',      headerAlign: 'center', align: 'center', disableColumnMenu: true,
				valueFormatter: (v) => v ?? '—' },
			{ field: 'queueMessage', headerName: 'Mensagem',   headerAlign: 'center', align: 'left',   disableColumnMenu: true, flex: 1 },
		]

		return (
			<Stack spacing={2} margin={1} mt={2} sx={{ flex: 1 }}>
				{returnStatus > 0 && (
					<Alert severity={returnStatus === 1 ? 'success' : 'error'} onClose={() => setReturnStatus(0)}>
						{returnMessage}
					</Alert>
				)}

				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
					<TextField
						size="small" label="Ticker" value={ticker} sx={{ width: 130 }}
						onChange={e => setTicker(e.target.value)}
					/>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel>Tipo</InputLabel>
						<Select value={type} label="Tipo" onChange={e => setType(e.target.value)}>
							<MenuItem value="">Todos</MenuItem>
							<MenuItem value="ACAO">Ações</MenuItem>
							<MenuItem value="STOCK">Stocks</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>País</InputLabel>
						<Select value={country} label="País" onChange={e => setCountry(e.target.value)}>
							<MenuItem value="">Todos</MenuItem>
							{countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControlLabel
						control={<Switch size="small" checked={notQueued} onChange={e => setNotQueued(e.target.checked)} />}
						label="Não enfileirado"
					/>
					<Button variant="contained" onClick={handleFilter}>Filtrar</Button>
					<Button variant="contained" onClick={handleClear}>Limpar</Button>
				</Stack>

				{filteredItems
					? <DataGrid
						getRowId={(row) => row.ticker + row.quarter}
						rows={filteredItems}
						columns={columns}
						slots={{ footer: PendingUpdatesFooter }}
						initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
						pageSizeOptions={[10, 20, 50]}
						rowHeight={30}
						checkboxSelection
						onRowSelectionModelChange={selected => setCheckboxSelection(selected)}
					/>
					: <Loading sx={{ flex: 1 }} />
				}
			</Stack>
		)
	}

	return (
		<Paper sx={{ marginLeft: 2, marginRight: 2, marginBottom: 2, minHeight: '88vh', display: 'flex', flexDirection: 'column' }} elevation={2}>

			<Tabs value={currentTabIndex} onChange={handleTabChange}>
				<Tab label="Itens na fila"/>
				<Tab label="Itens na fila rápida"/>
				<Tab label="Itens com erro"/>
				<Tab label="Aguardando atualização"/>
				<Tab label="Logs de Operação"/>
			</Tabs>

			{currentTabIndex === 0 && <QueueItemsGrid/>}
			{currentTabIndex === 1 && <FastQueueGrid/>}
			{currentTabIndex === 2 && <QueueErrorsGrid/>}
			{currentTabIndex === 3 && <PendingUpdatesGrid/>}
			{currentTabIndex === 4 && <OperationLogs/>}

		</Paper>
	)
}

export default OperationPanel